import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { fetchDocsNav } from '../../../Api/Docs';
import { DocsNavItem } from '../../../models/docs';
import { DocsNavList } from './DocsNavList';
import { DocsContent } from './DocsContent';
import { DocsToc } from './DocsToc';
import { DocsTocEntry } from './docsHeadings';
import { DocsRoleSimulator, DOCS_ROLE_SIM_ENABLED } from './DocsRoleSimulator';

const MIN_SIDEBAR_WIDTH = 220;
const MAX_SIDEBAR_WIDTH = 480;
const DEFAULT_SIDEBAR_WIDTH = 288;
const TOC_WIDTH = 240;

// Returns the slug encoded in the current /docs/* path (empty string at /docs).
function slugFromPath(pathname: string): string {
  return pathname.replace(/^\/docs\/?/, '').replace(/\/$/, '');
}

// First navigable slug in the tree, used to land the bare /docs route on a page.
function firstSlug(items: DocsNavItem[]): string | undefined {
  for (const item of items) {
    if (item.slug) return item.slug;
    const childSlug = firstSlug(item.children);
    if (childSlug) return childSlug;
  }
  return undefined;
}

interface DocsViewProps {
  // Only UPSTACK staff may preview the docs as another role; the API ignores the role for anyone else.
  canSimulateRole?: boolean;
}

// Full-screen documentation viewer with no main-app chrome. Layout: a resizable
// left navigation sidebar (a temporary drawer on small screens), the page
// content, and an optional right-hand table of contents on wide screens.
export const DocsView: FunctionComponent<DocsViewProps> = ({ canSimulateRole = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showToc = useMediaQuery(theme.breakpoints.up('lg'));

  const [navItems, setNavItems] = useState<DocsNavItem[]>([]);
  const [navLoading, setNavLoading] = useState<boolean>(true);
  const [navError, setNavError] = useState<string | null>(null);
  // Non-production docs role simulation; always '' (no simulation) in production
  // since the selector that sets it is gated out of the prod bundle.
  const [simulateRole, setSimulateRole] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [tocEntries, setTocEntries] = useState<DocsTocEntry[]>([]);
  const [sidebarWidth, setSidebarWidth] = useState<number>(DEFAULT_SIDEBAR_WIDTH);
  const resizingRef = useRef<boolean>(false);
  const mouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const mouseUpHandlerRef = useRef<(() => void) | null>(null);

  const activeSlug = useMemo(() => slugFromPath(location.pathname), [location.pathname]);

  useEffect(() => {
    const controller = new AbortController();
    setNavLoading(true);
    setNavError(null);
    fetchDocsNav(simulateRole, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      if (result.error || !result.data) {
        setNavError(result.error?.message || 'The documentation navigation could not be loaded.');
      } else {
        setNavItems(result.data.items);
      }
      setNavLoading(false);
    });
    return () => controller.abort();
  }, [simulateRole]);

  // Land the bare /docs route on the first available page.
  useEffect(() => {
    if (!activeSlug && navItems.length > 0) {
      const target = firstSlug(navItems);
      if (target) navigate(`/docs/${target}`, { replace: true });
    }
  }, [activeSlug, navItems, navigate]);

  const handleSelect = useCallback(
    (slug: string) => {
      navigate(`/docs/${slug}`);
      setDrawerOpen(false);
    },
    [navigate]
  );

  const startResizing = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    resizingRef.current = true;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const width = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, moveEvent.clientX));
      setSidebarWidth(width);
    };
    const onMouseUp = () => {
      resizingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      mouseMoveHandlerRef.current = null;
      mouseUpHandlerRef.current = null;
      document.body.style.userSelect = '';
    };

    mouseMoveHandlerRef.current = onMouseMove;
    mouseUpHandlerRef.current = onMouseUp;

    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  // Remove any dangling drag listeners if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      if (mouseMoveHandlerRef.current) window.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      if (mouseUpHandlerRef.current) window.removeEventListener('mouseup', mouseUpHandlerRef.current);
      document.body.style.userSelect = '';
    };
  }, []);

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700 }}>
          Documentation
        </Typography>
        <Button
          size="small"
          onClick={() => navigate('/')}
          sx={{ mt: 0.5, px: 0, justifyContent: 'flex-start', color: 'text.secondary' }}>
          ← Back to Portal
        </Button>
      </Box>
      {DOCS_ROLE_SIM_ENABLED && canSimulateRole && (
        <DocsRoleSimulator
          value={simulateRole}
          onChange={setSimulateRole}
        />
      )}
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        {navLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!navLoading && navError && (
          <Box sx={{ px: 2 }}>
            <Alert severity="error">{navError}</Alert>
          </Box>
        )}
        {!navLoading && !navError && (
          <DocsNavList
            items={navItems}
            activeSlug={activeSlug}
            onSelect={handleSelect}
          />
        )}
      </Box>
    </Box>
  );

  const mainContent = (
    <Box
      component="main"
      sx={{ flexGrow: 1, overflowY: 'auto', minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', px: { xs: 2, md: 4 }, py: 4 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: 900 }}>
          <DocsContent
            slug={activeSlug}
            simulateRole={simulateRole}
            onTocChange={setTocEntries}
          />
        </Box>
        {showToc && (
          <Box
            sx={{
              width: TOC_WIDTH,
              flexShrink: 0,
              ml: 4,
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start'
            }}>
            <DocsToc entries={tocEntries} />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      data-testid="docs-view"
      sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      {isMobile && (
        <AppBar
          position="static"
          color="default"
          elevation={1}>
          <Toolbar variant="dense">
            <IconButton
              edge="start"
              aria-label="Open documentation navigation"
              onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ ml: 1, fontWeight: 700 }}>
              Documentation
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              onClick={() => navigate('/')}
              sx={{ color: 'text.secondary' }}>
              ← Portal
            </Button>
          </Toolbar>
        </AppBar>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        {isMobile ? (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: DEFAULT_SIDEBAR_WIDTH } }}>
            {sidebarContent}
          </Drawer>
        ) : (
          <>
            <Box
              component="aside"
              sx={{
                width: sidebarWidth,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                height: '100%'
              }}>
              {sidebarContent}
            </Box>
            <Box
              role="separator"
              aria-orientation="vertical"
              onMouseDown={startResizing}
              sx={{
                width: '6px',
                cursor: 'col-resize',
                flexShrink: 0,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            />
          </>
        )}
        {mainContent}
      </Box>
    </Box>
  );
};
